import React, { ReactNode } from 'react';
import { ColumnsType } from 'antd/lib/table';
import { Location } from 'react-router-dom';
import { SortOrder } from 'antd/es/table/interface';
import { BuyerCode, Code, Image } from 'common/components';
import { EPresetType, EStorageKey } from 'common/const';
import { ESCAPE_REGEX_PATTERN } from 'common/config';
import { IAccountShort } from 'common/models';
import { ArrowUpLong, Cart, Sort } from 'common/svg';
import { getPriceToDisplay, priceFormatter, stringToNumber } from 'common/helpers';
import { getStorageItem } from 'common/helpers/axios.helper';
import { getIsSelfRegistered } from 'entities/Account/Account.helper';
import { ICatalog } from 'entities/Catalog/Catalog.models';
import { IGood, IGoodListPayload, IGoodPosition } from 'entities/Goods/Goods.models';
import { GoodDiscount } from 'entities/Goods/components';
import { ICategory } from 'entities/Category/Category.models';
import { IProperty } from 'entities/Property/Property.models';
import { getPropertyPresetType } from 'entities/Property/Property.helper';
import { IUser } from 'entities/User/User.models';

export const getCatalogMenuItems = (list: ICatalog[]) => list.map(({ id, name }) => ({ key: id, label: name }));

export const getCurrentCatalog = (list: ICatalog[], id?: string) => list.find((catalog) => catalog.id === Number(id));

export const getCatalogGoodsListPayloadAvailabilityFilters = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const isAvailableForCustomerParam = searchParams.get('all-assortment');
  const isAvailableForPurchaseParam = searchParams.get('all-goods');

  return {
    isAvailableForCustomer: isAvailableForCustomerParam === 'true' ? undefined : true,
    isAvailableForPurchase:
      isAvailableForCustomerParam === 'true' ? undefined : isAvailableForPurchaseParam === 'true' ? undefined : true,
  };
};

export const generateCatalogSearchValueHighlightedText = (name: string, search: string) => {
  if (!search.length) {
    return <span className="text-lists">{name}</span>;
  }

  const parts = name.split(new RegExp(`(${search.replace(ESCAPE_REGEX_PATTERN, '\\$&')})`, 'gi'));

  return (
    <span>
      {parts.map((part, index) => {
        return part.toLowerCase() === search.toLowerCase() ? (
          <span key={index} className="text-accent color-black">
            {part}
          </span>
        ) : (
          <span key={index} className="text-lists color-black">
            {part}
          </span>
        );
      })}
    </span>
  );
};

export const renderCatalogSearchDropdownProductsRecords = (search: string) => {
  return [
    {
      key: 'image',
      render: (_, { id, images }) => (
        <a href={`/good/${id}`} target="_blank" rel="noopener noreferrer">
          <Image size={40} src={images[0]} />
        </a>
      ),
      width: 48,
    },
    {
      key: 'name',
      dataIndex: 'name',
      render: (_, { id, name }) => {
        return (
          <a href={`/good/${id}`} target="_blank" rel="noopener noreferrer">
            <div className="ellipsis-2">{generateCatalogSearchValueHighlightedText(name, search)}</div>
          </a>
        );
      },
      width: 556,
    },
    {
      key: 'vendorCode',
      render: (_, { id, buyerCode }) => (
        <a href={`/good/${id}`} target="_blank" rel="noopener noreferrer">
          <BuyerCode code={buyerCode} />
        </a>
      ),
      onCell: () => ({ className: 'text-center' }),
    },
    {
      key: 'sellerCode',
      dataIndex: 'sellerCode',
      render: (_, { id, sellerCode }) => (
        <a href={`/good/${id}`} target="_blank" rel="noopener noreferrer">
          <span className="ff-regular fs-xs color-dark-grey">{sellerCode}</span>
        </a>
      ),
      onCell: () => ({ className: 'text-right' }),
    },
  ] as ColumnsType<IGood>;
};

export const getCatalogGoodsAvailabilityFilters = (location: Location, isSeller?: boolean, account?: IAccountShort) => {
  let isAvailableForCustomer: boolean | undefined = undefined;
  let isAvailableForPurchase: boolean | undefined = undefined;
  const xBasketId = getStorageItem(EStorageKey.XBasketId);
  const isSelfRegistered = getIsSelfRegistered(account);

  if (!isSeller || (isSeller && xBasketId)) {
    if (location.state === null) {
      isAvailableForCustomer = true;

      if (!isSelfRegistered) {
        isAvailableForPurchase = true;
      }
    } else {
      isAvailableForCustomer = location.state.isAvailableForCustomer;
      isAvailableForPurchase = location.state.isAvailableForPurchase;
    }
  }

  return { isAvailableForCustomer, isAvailableForPurchase };
};

export const renderCatalogGoodsListRecords = ({
  searchNotEmpty,
  category,
  user,
}: {
  searchNotEmpty?: boolean;
  category?: ICategory | null;
  user: IUser | null;
}) => {
  const sortIcon = ({ sortOrder }: { sortOrder: SortOrder }): ReactNode | null => {
    switch (sortOrder) {
      case 'ascend': {
        return <ArrowUpLong className="icon-arrow-up-long-bright-green" />;
      }
      case 'descend': {
        return <ArrowUpLong className="icon-arrow-up-long-bright-green" style={{ rotate: '180deg' }} />;
      }
      default: {
        return null;
      }
    }
  };

  const records: ColumnsType<IGood> = [
    {
      key: 'image',
      title: <Sort className="icon-sort-dark-grey" />,
      fixed: 'left',
      width: 124,
      render: (_, { images }) => <Image size={104} src={images[0]} />,
    },
    {
      key: 'name',
      title: 'Название',
      fixed: 'left',
      render: (_, { name, sellerCode, buyerCode }) => {
        return (
          <>
            <span className="mb-4 ff-medium fs-m">{name}</span>

            <div className="ellipsis">
              <span className="mr-6">
                <Code sellerCode={sellerCode} />
              </span>

              <BuyerCode code={buyerCode} />
            </div>
          </>
        );
      },
      sorter: true,
      sortIcon,
      width: 540,
      onCell: () => ({ style: { minWidth: 300 } }),
    },
  ];

  if (searchNotEmpty) {
    records.push({
      key: 'category',
      title: 'Категории',
      dataIndex: 'categoryName',
      render: (_, record) => {
        if (!record.categories) {
          return null;
        }

        const categoryNames = record.categories.map((item) => item.categoryName);

        return <span className="ff-regular fs-m">{categoryNames.join(', ')}</span>;
      },
    });
  }

  if (category) {
    category.propertiesToDisplay?.forEach((propertyToDisplay) => {
      records.push({
        key: propertyToDisplay.propertyId,
        title: propertyToDisplay.propertyName,
        render: (_, { properties }) => {
          const propertyItem = properties.find((property) => property.propertyId === propertyToDisplay.propertyId);

          if (!propertyItem) {
            return null;
          }

          return (
            <span className="ff-regular fs-m word-break-all">{`${propertyItem.value} ${propertyItem.unitOfMeasurement ?? ''}`}</span>
          );
        },
        onCell: () => ({ style: { minWidth: 150, maxWidth: 300 } }),
        onHeaderCell: () => ({ style: { whiteSpace: 'normal' } }),
      });
    });
  }

  records.push(
    {
      key: 'brand',
      title: 'Бренд',
      dataIndex: 'brand',
      render: (_, record) => {
        if (!record.brand) {
          return null;
        }

        return <span className="ff-regular fs-m">{record.brand}</span>;
      },
      onCell: () => ({ style: { minWidth: 'max-content' } }),
    },
    {
      key: 'price',
      title: 'Цена',
      fixed: 'right',
      render: (_, { discountValue = 0, price, priceWithTaxes, originalPrice, originalPriceWithTaxes, goodInBasket }) => {
        const isSelfRegistered = getIsSelfRegistered(user?.account);
        const showDiscount = discountValue > 0;
        const priceToDisplay = getPriceToDisplay(price, priceWithTaxes, user?.account);
        const originalPriceToDisplay = getPriceToDisplay(originalPrice, originalPriceWithTaxes, user?.account);

        return (
          <>
            {showDiscount && <GoodDiscount discount={discountValue} min />}

            <div>
              {showDiscount && (
                <span className="mr-3 ff-medium fs-xs color-dark-grey text-decoration-through">
                  {priceFormatter(originalPriceToDisplay)}
                </span>
              )}
              <span className="ff-medium fs-s">{priceFormatter(priceToDisplay)}</span>
            </div>

            {!isSelfRegistered && (
              <div className="ff-regular fs-xs color-dark-grey">{`с НДС: ${priceFormatter(priceWithTaxes)}`}</div>
            )}

            {goodInBasket && (
              <div className="catalog-goods-list-item-in-basket-label">
                <Cart className="icon-cart-bright-green" />

                <span className="ff-medium fs-xs color-bright-green">В корзине</span>
              </div>
            )}
          </>
        );
      },
      onCell: () => ({ style: { minWidth: 'max-content' } }),
    },
  );

  return records;
};

export const getCatalogEmptyStateContent = ({
  payload,
  categoryId,
  account,
}: {
  payload: IGoodListPayload;
  categoryId?: number;
  account?: IAccountShort;
}) => {
  const isSelfRegistered = getIsSelfRegistered(account);
  let emptyTitle = '';
  let emptyDescription = '';
  let emptyBackBtnTitle = '';

  if (!isSelfRegistered && payload.isAvailableForCustomer) {
    emptyTitle = 'Доступные для заказа товары не найдены';
    emptyDescription = 'Попробуйте упростить запрос или посмотреть весь ассортимент поставщика.';
    emptyBackBtnTitle = 'Показать весь ассортимент';
  } else {
    if (categoryId) {
      if (payload.positions?.length) {
        emptyTitle = 'По этим параметрам товары не найдены.';
        emptyDescription = 'Они появятся здесь после добавления поставщиком.';
        emptyBackBtnTitle = '';
      } else {
        emptyTitle = 'В этой категории товары отсутствуют.';
        emptyDescription = 'Они появятся здесь после добавления поставщиком.';
        emptyBackBtnTitle = 'Вернуться назад';
      }
    } else {
      emptyTitle = 'По вашему запросу ничего не найдено.';
      emptyDescription = 'Попробуйте найти товар вручную.';
      emptyBackBtnTitle = 'Назад в каталог';
    }
  }

  return { emptyTitle, emptyDescription, emptyBackBtnTitle };
};

export const propertyListToCatalogGoodsListPositions = (propertyList: IProperty[]) => {
  return propertyList
    .filter((property) => !!property.result?.length)
    .map((property) => {
      const position: IGoodPosition = { propertyId: property.id };

      if (property.presetType !== EPresetType.Range) {
        position.values = property.result;
      } else {
        position.range = {
          from: property.result?.[0] ? Number(property.result[0]) : undefined,
          to: property.result?.[1] ? Number(property.result[1]) : undefined,
        };
      }

      return position;
    });
};

export const mapCatalogPropertyListToChips = (propertyList: IProperty[]) => {
  return propertyList.flatMap((property) => {
    const { isSingleValue, isMultipleValue, isCheckbox, isRange } = getPropertyPresetType(property);
    const { id, isCategory, displayName, result, unitOfMeasurement } = property;
    const unit = unitOfMeasurement ? unitOfMeasurement : '';

    if (isCategory || !result?.length) {
      return null;
    }

    if (isSingleValue) {
      const value = result?.[0];

      return { id, name: displayName, value: value, unit };
    }

    if (isMultipleValue || isCheckbox) {
      return result.map((value) => {
        return { id, name: displayName, value: value, unit };
      });
    }

    if (isRange) {
      const from = result[0];
      const to = result[1];
      let value = '';

      if (from) {
        value += `от ${from}`;
      }

      if (to) {
        value += ` до ${to}`;
      }

      return { id, name: displayName, value: value, unit };
    }

    return null;
  });
};

export const searchParamsToCatalogGoodsListPositions = (properties: IProperty[] = []) => {
  const searchParams = new URLSearchParams(window.location.search);
  const positions: IGoodPosition[] = [];

  searchParams.forEach((value, key) => {
    const propertyId = stringToNumber(key);
    const property = properties.find((item) => item.id === propertyId);

    if (!property) {
      return;
    }

    if (property.presetType === EPresetType.Range) {
      const parsedValue: string[] = JSON.parse(value);

      positions.push({
        propertyId,
        range: {
          from: parsedValue[0].length ? stringToNumber(parsedValue[0]) : undefined,
          to: parsedValue[1].length ? stringToNumber(parsedValue[1]) : undefined,
        },
      });
      return;
    }

    positions.push({
      propertyId,
      values: JSON.parse(value),
    });
  });

  return positions;
};

export const buildCatalogUrlParams = (positions: IGoodPosition[]): URLSearchParams => {
  const params = new URLSearchParams();

  positions.forEach((item) => {
    if (item.range) {
      params.append(`f_${item.propertyId}`, JSON.stringify({ t: 'r', from: item.range.from, to: item.range.to }));
    } else {
      item.values?.forEach((v) => {
        params.append(`f_${item.propertyId}`, JSON.stringify(v));
      });
    }
  });

  return params;
};
